import { ArrowLeftOutlined, ScheduleOutlined, SearchOutlined, AppstoreOutlined, DownloadOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, DatePicker, Input, Layout, Select, Space, Switch, Table, Tag, Typography, Modal, Checkbox, Divider, notification } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { AppConstants } from "../../../Appconstants";
import Loader from "../../../Home/Loader/Loader";
import { getSalesDashboardDetails } from "../../../redux/actions/API/dashboardActions";
import { getItems } from "../../../redux/actions/API/items";
import { getUOM } from "../../../redux/actions/API/uomAction";
import { getSetting } from "../../../redux/actions/API/settingAction";
import noResult from "../../../Utility/images/noresult.png";
import { isValidJSON } from "../../utility/itemsUtility";

const { RangePicker } = DatePicker;
const { Content } = Layout;
const { Text } = Typography;

interface ISalesDashboardDetailsProps {
  companyId: any;
  getSalesDashboardDetails: Function;
  salesDetailsData: any;
  loading: boolean;
  itemsData?: any;
  getItems?: Function;
  uomData?: any;
  getUOM?: Function;
  settingData?: any;
  getSetting?: Function;
}

const SALES_TYPES = [
  { label: "Sales Orders", value: 0, typeKey: "salesOrder", color: "#6366f1", bg: "rgba(99, 102, 241, 0.1)" },
  { label: "Today's Deliveries", value: 1, typeKey: "todayDeliveries", color: "#FFB26B", bg: "rgba(255, 178, 107, 0.1)" },
  { label: "Delivery Challans", value: 2, typeKey: "deliveryChallan", color: "#A084E8", bg: "rgba(160, 132, 232, 0.1)" },
  { label: "Invoices", value: 3, typeKey: "invoice", color: "#FF92A5", bg: "rgba(255, 146, 165, 0.1)" },
  { label: "Pending Dispatches", value: 4, typeKey: "pendingDispatch", color: "#FFD86E", bg: "rgba(255, 216, 110, 0.1)" },
  { label: "Partially Delivered Dispatches", value: 5, typeKey: "partiallyDeliveredDispatch", color: "#FFB26B", bg: "rgba(255, 178, 107, 0.1)" },
  { label: "Fully Delivered Dispatches", value: 6, typeKey: "fullyDeliveredDispatch", color: "#73C7C7", bg: "rgba(115, 199, 199, 0.1)" },
  { label: "Cancelled Dispatches", value: 7, typeKey: "cancelledDispatch", color: "#FF92A5", bg: "rgba(255, 146, 165, 0.1)" },
  { label: "Purchase Orders", value: 8, typeKey: "purchaseOrder", color: "#6366f1", bg: "rgba(99, 102, 241, 0.1)" },
  { label: "Purchase Invoices", value: 9, typeKey: "purchaseInvoice", color: "#ec4899", bg: "rgba(236, 72, 153, 0.1)" },
];

/**
 * Recursively extracts plain text from React nodes, JSX elements, arrays, and objects.
 * Essential for exporting tables to XLSX where HTML/JSX cannot be rendered.
 */
const extractTextFromReactNode = (node: any): string => {
  if (node === null || node === undefined) return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (typeof node === "boolean") return node ? "Yes" : "No";

  // Handle Ant Design Table custom render returning { children, props }
  if (typeof node === "object" && !React.isValidElement(node) && "children" in node) {
    return extractTextFromReactNode((node as any).children);
  }

  // Handle Arrays of nodes
  if (Array.isArray(node)) {
    const isAllPrimitive = node.every(
      (item) => typeof item === "string" || typeof item === "number" || item === null || item === undefined
    );
    if (isAllPrimitive) {
      return node.map((item) => (item === null || item === undefined ? "" : String(item))).join("");
    }
    return node
      .map((child) => extractTextFromReactNode(child).trim())
      .filter(Boolean)
      .join(", ");
  }

  // Handle React Elements (e.g. <Tag>, <Link>, <span>, <div>)
  if (React.isValidElement(node)) {
    const props = (node.props as any) || {};
    return extractTextFromReactNode(props.children);
  }

  // Handle Objects (Dates, label objects)
  if (typeof node === "object") {
    if (node instanceof Date) return node.toLocaleDateString("en-IN");
    if (node.label) return String(node.label);
    if (node.name) return String(node.name);
    return "";
  }

  return String(node);
};

/**
 * Strips all HTML tags, decodes HTML entities, and collapses whitespaces to return pure plain text.
 */
const convertToCleanPlainText = (val: any): string => {
  const extracted = extractTextFromReactNode(val);
  if (!extracted) return "";

  // Strip all HTML tags
  let text = extracted.replace(/<[^>]+>/g, " ");

  // Decode common HTML entities
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");

  // Normalize all whitespace
  return text.replace(/\s+/g, " ").trim();
};

const SalesDashboardDetails: React.FC<ISalesDashboardDetailsProps> = ({
  companyId,
  getSalesDashboardDetails,
  salesDetailsData,
  loading,
  itemsData,
  getItems,
  uomData,
  getUOM,
  settingData,
  getSetting,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Parse typeIndices array from URL search params (or fallback to typeIndex)
  const selectedTypeIndices = useMemo(() => {
    const indicesParam = searchParams.get("typeIndices");
    const singleIndexParam = searchParams.get("typeIndex");

    if (indicesParam !== null) {
      const parsed = indicesParam
        .split(",")
        .map((v) => Number(v.trim()))
        .filter((val) => Number.isInteger(val) && val >= 0 && val < SALES_TYPES.length);
      if (parsed.length > 0) return parsed;
    } else if (singleIndexParam !== null) {
      const val = Number(singleIndexParam);
      if (Number.isInteger(val) && val >= 0 && val < SALES_TYPES.length) {
        return [val];
      }
    }
    return [0];
  }, [searchParams]);

  const selectedTypes = useMemo(() => {
    return selectedTypeIndices.map((idx) => SALES_TYPES[idx]);
  }, [selectedTypeIndices]);

  const isDispatchBasedOnChallan = useMemo(() => {
    const dispatchStatusBasedOnChallanParam = searchParams.get("dispatchStatusBasedOnChallan");
    if (dispatchStatusBasedOnChallanParam !== null) {
      return dispatchStatusBasedOnChallanParam === "true";
    }
    if (settingData?.dispatchStatusBasedOnChallan !== undefined && settingData?.dispatchStatusBasedOnChallan !== null) {
      return Boolean(settingData.dispatchStatusBasedOnChallan);
    }
    return true;
  }, [searchParams, settingData?.dispatchStatusBasedOnChallan]);

  const [globalSearchText, setGlobalSearchText] = useState("");
  const [itemSearchText, setItemSearchText] = useState("");
  const [dateRange, setDateRange] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Row selection & Split view states
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedItemRowKeys, setSelectedItemRowKeys] = useState<React.Key[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isSplitView, setIsSplitView] = useState<boolean>(false);

  // Grouping states
  const [openGroupingModal, setOpenGroupingModal] = useState<boolean>(false);
  const [applyGrouping, setApplyGrouping] = useState<boolean>(false);
  const [groupingKeys, setGroupingKeys] = useState<string[]>([]);
  const [groupingData, setGroupingData] = useState<any[]>([]);
  const [groupingColumns, setGroupingColumns] = useState<any[]>([]);
  const [selectedGroupedRowKeys, setSelectedGroupedRowKeys] = useState<React.Key[]>([]);
  const [selectedGroupedRows, setSelectedGroupedRows] = useState<any[]>([]);
  const [separateWithUnit, setSeparateWithUnit] = useState<boolean>(false);

  // Fetch item, UOM, and settings data
  useEffect(() => {
    if (companyId) {
      getItems?.({ companyId: Number(companyId) });
      getUOM?.({ companyId: Number(companyId) });
      getSetting?.({ companyId: Number(companyId) });
    }
  }, [companyId, getItems, getUOM, getSetting]);

  const isReduceStockOnDC = useMemo(() => {
    return Boolean(
      settingData?.["stockReduceOnDC"] === "true" ||
      settingData?.["stockReduceOnDC"] === true ||
      localStorage.getItem("stockReduceOnDC") === "true"
    );
  }, [settingData]);

  const isReduceStockOnIV = useMemo(() => {
    return Boolean(
      settingData?.["stockReduceOnIV"] === "true" ||
      settingData?.["stockReduceOnIV"] === true ||
      localStorage.getItem("stockReduceOnIV") === "true"
    );
  }, [settingData]);

  // Helper to calculate pending quantity based on inventory settings
  const calculatePendingQuantity = (soQty: number, challanQty: number, invQty: number) => {
    const qty = Number(soQty || 0);
    const cQty = Number(challanQty || 0);
    const iQty = Number(invQty || 0);

    if (isReduceStockOnDC && isReduceStockOnIV) {
      return Math.max(0, qty - (cQty + iQty));
    } else if (isReduceStockOnDC) {
      return Math.max(0, qty - cQty);
    } else if (isReduceStockOnIV) {
      return Math.max(0, qty - iQty);
    } else {
      return Math.max(0, qty - (cQty + iQty));
    }
  };

  const uomMap = useMemo(() => {
    if (!Array.isArray(uomData)) return {};
    return uomData.reduce((acc: any, curr: any) => {
      if (curr && curr.id !== undefined) {
        acc[curr.id] = curr.code || curr.name;
        acc[String(curr.id)] = curr.code || curr.name;
        if (curr.code) {
          acc[curr.code] = curr.code;
          acc[String(curr.code).toLowerCase()] = curr.code;
        }
        if (curr.name) {
          acc[curr.name] = curr.code || curr.name;
          acc[String(curr.name).toLowerCase()] = curr.code || curr.name;
        }
      }
      return acc;
    }, {});
  }, [uomData]);

  const itemsMap = useMemo(() => {
    const list = Array.isArray(itemsData) ? itemsData : Array.isArray(itemsData?.data) ? itemsData.data : [];
    return list.reduce((acc: any, curr: any) => {
      if (curr) {
        if (curr.itemId) {
          acc[curr.itemId] = curr;
          acc[String(curr.itemId).toLowerCase()] = curr;
        }
        if (curr.id) {
          acc[curr.id] = curr;
          acc[String(curr.id).toLowerCase()] = curr;
        }
        if (curr.itemCode) {
          acc[curr.itemCode] = curr;
          acc[String(curr.itemCode).toLowerCase()] = curr;
        }
        if (curr.itemName) {
          acc[curr.itemName] = curr;
          acc[String(curr.itemName).toLowerCase()] = curr;
        }
      }
      return acc;
    }, {});
  }, [itemsData]);

  // Helper to determine the conversion factor to base unit by checking if record.UOM is an alternate unit or base unit
  const getItemConversionFactorToBase = (record: any, itemInfo?: any) => {
    const info = itemInfo ||
      itemsMap[record?.itemId] ||
      itemsMap[String(record?.itemId || "").toLowerCase()] ||
      itemsMap[record?.itemName] ||
      itemsMap[String(record?.itemName || "").toLowerCase()] ||
      record;

    const docUOM = record?.UOM ?? record?.uom ?? record?.unit ?? record?.alternateUOM;

    const rawAltUnits = info?.alternateUnits || info?.alternateUnit || record?.alternateUnits || record?.alternateUnit || [];
    const altUnitsArray = Array.isArray(rawAltUnits) ? rawAltUnits : (rawAltUnits ? [rawAltUnits] : []);

    const masterPrimaryUnit = (info?.metricsUnit ? uomMap[info.metricsUnit] : null) ||
      info?.unit ||
      info?.uom ||
      info?.UOM ||
      record?.baseUnit ||
      record?.baseUOM;

    const norm = (v: any) => String(v ?? "").trim().toLowerCase();
    const docUOMStr = norm(docUOM);
    const docUOMNum = typeof docUOM === "number" ? docUOM : (!isNaN(Number(docUOM)) && docUOM !== "" ? Number(docUOM) : null);

    // If no document UOM specified, factor is 1
    if (!docUOMStr && docUOMNum === null) {
      return 1;
    }

    // 1. Check if docUOM matches Master Base (Primary) Unit
    const isBaseUnitMatch =
      (docUOMNum !== null && info?.metricsUnit !== undefined && Number(info.metricsUnit) === docUOMNum) ||
      (masterPrimaryUnit && docUOMStr === norm(masterPrimaryUnit)) ||
      (info?.metricsUnit && docUOMStr === norm(uomMap[info.metricsUnit])) ||
      (info?.unit && docUOMStr === norm(info.unit)) ||
      (info?.uom && docUOMStr === norm(info.uom)) ||
      (info?.UOM && docUOMStr === norm(info.UOM)) ||
      (record?.baseUnit && docUOMStr === norm(record.baseUnit));

    if (isBaseUnitMatch) {
      return 1;
    }

    // 2. Check if docUOM matches any Alternate Unit
    for (const alt of altUnitsArray) {
      if (!alt) continue;
      const factor = Number(alt.conversionfactor || alt.conversionFactor || alt.conversion_factor || 1);

      const altUnitId = alt.altUnitId ?? (typeof alt.alternateUnits === "number" ? alt.alternateUnits : null);
      const altUnitName = String(
        (typeof alt.alternateUnits === "number" ? uomMap[alt.alternateUnits] : alt.alternateUnits) ||
        (alt.altUnitId ? uomMap[alt.altUnitId] : null) ||
        alt.code ||
        alt.name ||
        alt.uom ||
        alt.unit ||
        (typeof alt === "string" ? alt : "")
      ).trim();

      const isAltMatch =
        (docUOMNum !== null && (
          (altUnitId !== null && Number(altUnitId) === docUOMNum) ||
          (alt.alternateUnits !== undefined && Number(alt.alternateUnits) === docUOMNum) ||
          (alt.uom !== undefined && Number(alt.uom) === docUOMNum)
        )) ||
        (altUnitName && docUOMStr === norm(altUnitName)) ||
        (alt.code && docUOMStr === norm(alt.code)) ||
        (alt.name && docUOMStr === norm(alt.name)) ||
        (alt.alternateUnits && docUOMStr === norm(alt.alternateUnits)) ||
        (alt.uom && docUOMStr === norm(alt.uom)) ||
        (typeof alt === "string" && docUOMStr === norm(alt));

      if (isAltMatch) {
        return factor > 0 ? factor : 1;
      }
    }

    // 3. Fallback: If not matched to base unit, but record explicitly has conversionFactor > 1
    const directFactor = Number(record?.conversionFactor || record?.conversionfactor || 0);
    if (directFactor > 0 && directFactor !== 1) {
      return directFactor;
    }

    return 1;
  };

  // Helper to render quantity in all units (primary + alternate units)
  const renderQuantityWithAllUnits = (qty: number, record: any) => {
    let numQty = Number(qty || 0);
    const itemInfo = itemsMap[record?.itemId] ||
      itemsMap[String(record?.itemId || "").toLowerCase()] ||
      itemsMap[record?.itemName] ||
      itemsMap[String(record?.itemName || "").toLowerCase()] ||
      record;
    const masterPrimaryUnit = (itemInfo?.metricsUnit ? uomMap[itemInfo.metricsUnit] : null) ||
      itemInfo?.unit ||
      itemInfo?.uom ||
      itemInfo?.UOM;
    const primaryUnit = (record?.separateWithUnit && record.unit && record.unit !== "N/A")
      ? record.unit
      : (masterPrimaryUnit || (record?.unit && record.unit !== "N/A" ? record.unit : "") || (record?.UOM && record.UOM !== "N/A" ? record.UOM : ""));

    // If record is grouped with separateWithUnit, render directly in that unit without alternate conversions
    if (record?.separateWithUnit) {
      return (
        <div style={{ textAlign: "right" }}>
          <span style={{ fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>
            {numQty.toLocaleString("en-IN", { maximumFractionDigits: 2 })}{primaryUnit ? ` ${primaryUnit}` : ""}
          </span>
        </div>
      );
    }

    // If record is an un-grouped document item and was entered in an alternate unit, convert to base unit
    if (!record?.isBaseUnit && !record?.groupKey) {
      const factorToBase = getItemConversionFactorToBase(record, itemInfo);
      if (factorToBase && factorToBase !== 1) {
        numQty = numQty * factorToBase;
      }
    }

    const rawAltUnits = itemInfo?.alternateUnits || itemInfo?.alternateUnit || record?.alternateUnits || record?.alternateUnit || [];
    const altUnitsArray = Array.isArray(rawAltUnits) ? rawAltUnits : (rawAltUnits ? [rawAltUnits] : []);

    const seenUnits = new Set<string>();
    if (primaryUnit) {
      seenUnits.add(String(primaryUnit).trim().toLowerCase());
    }

    const uniqueAltUnits: any[] = [];
    altUnitsArray.forEach((alt: any) => {
      if (!alt) return;
      const altUnitName = String(
        (typeof alt.alternateUnits === "number" ? uomMap[alt.alternateUnits] : alt.alternateUnits) ||
        (alt.altUnitId ? uomMap[alt.altUnitId] : null) ||
        alt.code ||
        alt.name ||
        (typeof alt === "string" ? alt : "")
      ).trim();

      if (!altUnitName) return;
      const lowerName = altUnitName.toLowerCase();
      if (!seenUnits.has(lowerName)) {
        seenUnits.add(lowerName);
        uniqueAltUnits.push({ ...alt, resolvedUnitName: altUnitName });
      }
    });

    return (
      <div style={{ textAlign: "right", display: "flex", flexDirection: "column", gap: "4px" }}>
        <div style={{ fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>
          {numQty.toLocaleString("en-IN", { maximumFractionDigits: 2 })}{primaryUnit ? ` ${primaryUnit}` : ""}
        </div>
        {uniqueAltUnits.map((alt: any, idx: number) => {
          const factor = Number(alt.conversionfactor || alt.conversionFactor || alt.conversion_factor || 1);
          const convertedQty = factor ? (numQty / factor) : numQty;
          const altUnitName = alt.resolvedUnitName || "Unit";

          return (
            <div key={idx}>
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#2563eb",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "4px",
                  padding: "1px 6px",
                  display: "inline-block",
                  lineHeight: "16px",
                  whiteSpace: "nowrap"
                }}
              >
                {Number(convertedQty.toFixed(2)).toLocaleString("en-IN", { maximumFractionDigits: 2 })} {altUnitName}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Fetch data from backend when selectedTypeIndices, companyId, or dateRange changes
  useEffect(() => {
    if (!companyId) return;

    let start: string | undefined;
    let end: string | undefined;
    if (dateRange && dateRange.length === 2) {
      start = dateRange[0] ? dateRange[0].startOf("day").toISOString() : undefined;
      end = dateRange[1] ? dateRange[1].endOf("day").toISOString() : undefined;
    }

    const typeKeys = selectedTypes.map((t) => t.typeKey);

    getSalesDashboardDetails({
      companyId: Number(companyId),
      types: typeKeys,
      type: typeKeys[0], // fallback for backward compatibility
      dispatchStatusBasedOnChallan: isDispatchBasedOnChallan,
      ...(start && end ? { start, end } : {}),
    });
  }, [companyId, selectedTypeIndices, dateRange, getSalesDashboardDetails, isDispatchBasedOnChallan]);

  // Handle multi-select dropdown change
  const handleDropdownChange = (values: number[]) => {
    if (!values || values.length === 0) {
      navigate(`/salesDashboardDetails?typeIndices=0`);
    } else {
      navigate(`/salesDashboardDetails?typeIndices=${values.join(",")}`);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Safe data extraction with fallback
  const rawData = useMemo(() => {
    if (Array.isArray(salesDetailsData)) return salesDetailsData;
    if (Array.isArray(salesDetailsData?.data)) return salesDetailsData.data;
    if (Array.isArray(salesDetailsData?.items)) return salesDetailsData.items;
    return [];
  }, [salesDetailsData]);

  // Sync selected record when data loads or changes
  useEffect(() => {
    if (rawData.length > 0 && !selectedRecord) {
      setSelectedRecord(rawData[0]);
    }
  }, [rawData, selectedRecord]);

  // Filtered and searched data
  const filteredData = useMemo(() => {
    const searchText = globalSearchText.trim().toLowerCase();
    if (!searchText) return rawData;

    return rawData.filter((record: any) => {
      const docNo = String(record?.documentNumber || "").toLowerCase();
      const buyer = String(record?.buyerName || "").toLowerCase();
      const supplier = String(record?.supplierName || "").toLowerCase();
      const type = String(record?.documentType || "").toLowerCase();
      const tags = Array.isArray(record?.labels)
        ? record?.labels
        : (isValidJSON(record?.labels) || []);
      const labelsStr = tags.map((t: any) => (typeof t === "string" ? t : t?.label || "")).join(" ").toLowerCase();
      return (
        docNo.includes(searchText) ||
        buyer.includes(searchText) ||
        supplier.includes(searchText) ||
        type.includes(searchText) ||
        labelsStr.includes(searchText)
      );
    });
  }, [rawData, globalSearchText]);

  // Flatten items across all documents (or across selected document rows if checked)
  const allFlattenedItems = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const targetDocs =
      selectedRowKeys.length > 0
        ? filteredData.filter((doc: any) => {
          const key = doc.id || doc.documentNumber;
          return selectedRowKeys.includes(key);
        })
        : filteredData;

    const itemsList: any[] = [];

    targetDocs.forEach((doc: any) => {
      let rawItems = doc.items || doc.itemDetails || doc.documentItems;
      if (!rawItems) return;
      if (typeof rawItems === "string") {
        try {
          const parsed = JSON.parse(rawItems);
          if (Array.isArray(parsed)) rawItems = parsed;
          else rawItems = [];
        } catch {
          rawItems = [];
        }
      }

      if (Array.isArray(rawItems)) {
        rawItems.forEach((item: any, index: number) => {
          const docKey = doc.id || doc.documentNumber;
          const qty = Number(item?.quantity || item?.qty || 0);
          const rate = Number(item?.rate || item?.unitPrice || item?.price || 0);
          const taxPct = Number(item?.gstRate || item?.taxPercent || item?.tax || 0);

          const totalBeforeTax = Number(item?.totalBeforeTax || item?.itemValueBeforeTax || (qty * rate));
          const totalTax = Number(item?.totalTax || item?.taxValue || (totalBeforeTax * taxPct / 100));
          const totalAfterTax = Number(item?.totalAfterTax || item?.totalAmount || item?.amount || (totalBeforeTax + totalTax));

          const challanQty = Number(item?.receivedQuantity || item?.challanQuantity || item?.deliveredQuantity || 0);
          const invoiceQty = Number(item?.pendingQuantity || item?.invoiceQuantity || 0);
          const pendingQty = calculatePendingQuantity(qty, challanQty, invoiceQty);

          itemsList.push({
            ...item,
            uniqueKey: `${docKey}_item_${index}_${item.itemId || item.uniqueId || index}`,
            buyerName: doc.buyerName || doc.supplierName || "N/A",
            documentNumber: doc.documentNumber,
            documentDate: doc.createdAt || doc.documentDate,
            orderCreatedBy: doc.orderCreatedBy || doc.creator?.name || doc.createdBy || "N/A",
            purchaseOrderNumber: doc.purchaseOrderNumber || doc.poNumber || "N/A",
            purchaseOrderDate: doc.purchaseOrderDate || doc.poDate || "N/A",
            itemId: item.itemId || item.itemCode || item.uniqueId || `ITEM-${index + 1}`,
            itemName: item.itemName || item.name || item.description || "N/A",
            hsnCode: item.hsnCode || item.hsn || item.HSN || "N/A",
            unit: item.unit || item.UOM || item.uom || "N/A",
            UOM: item.UOM || item.uom || item.unit || "N/A",
            quantity: qty,
            challanQuantity: challanQty,
            receivedQuantity: challanQty,
            invoiceQuantity: invoiceQty,
            salesReturnQuantity: Number(item?.salesReturnQuantity || 0),
            pendingQuantity: pendingQty,
            price: rate,
            discountOne: Number(item?.discountOne || item?.discount || 0),
            tax: taxPct,
            totalBeforeTax: totalBeforeTax,
            totalTax: totalTax,
            totalAfterTax: totalAfterTax,
            parentStatus: doc.status,
            documentType: doc.documentType,
            store: doc.store || item.store
          });
        });
      }
    });

    return itemsList;
  }, [filteredData, selectedRowKeys]);

  // Filtered items inside right panel (searched by itemSearchText)
  const filteredItemsList = useMemo(() => {
    const text = itemSearchText.trim().toLowerCase();
    if (!text) return allFlattenedItems;

    return allFlattenedItems.filter((item: any) => {
      const docNo = String(item?.documentNumber || "").toLowerCase();
      const party = String(item?.buyerName || "").toLowerCase();
      const name = String(item?.itemName || "").toLowerCase();
      const code = String(item?.itemId || "").toLowerCase();
      const hsn = String(item?.hsnCode || "").toLowerCase();
      const poNo = String(item?.purchaseOrderNumber || "").toLowerCase();
      return (
        docNo.includes(text) ||
        party.includes(text) ||
        name.includes(text) ||
        code.includes(text) ||
        hsn.includes(text) ||
        poNo.includes(text)
      );
    });
  }, [allFlattenedItems, itemSearchText]);

  // Selected documents logic for MRP (document level)
  const selectedDocs = useMemo(() => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) return [];
    return filteredData.filter((doc: any) => {
      const key = doc.id || doc.documentNumber;
      return selectedRowKeys.includes(key);
    });
  }, [filteredData, selectedRowKeys]);

  // Selected item rows logic for MRP (item level)
  const selectedItemDocs = useMemo(() => {
    if (!selectedItemRowKeys || selectedItemRowKeys.length === 0) return [];
    return filteredItemsList.filter((item: any) => {
      const key = item.groupKey || item.uniqueKey || item.itemId;
      return selectedItemRowKeys.includes(key);
    });
  }, [filteredItemsList, selectedItemRowKeys]);

  const isAllSelectedSalesOrder = useMemo(() => {
    if (isSplitView) {
      return (
        selectedItemDocs.length > 0 &&
        selectedItemDocs.every(
          (item: any) =>
            item?.documentType === AppConstants?.DOCUMENT_TYPE?.SALES_ORDER ||
            item?.documentType === "Sales Order" ||
            item?.documentType === "salesOrder"
        )
      );
    }

    return (
      selectedDocs.length > 0 &&
      selectedDocs.every(
        (doc: any) =>
          doc?.documentType === AppConstants?.DOCUMENT_TYPE?.SALES_ORDER ||
          doc?.documentType === "Sales Order" ||
          doc?.documentType === "salesOrder"
      )
    );
  }, [isSplitView, selectedDocs, selectedItemDocs]);

  const isGroupedMRPVisible = useMemo(() => {
    return (
      isSplitView &&
      applyGrouping &&
      groupingKeys.includes("item") &&
      selectedGroupedRows.length > 0
    );
  }, [isSplitView, applyGrouping, groupingKeys, selectedGroupedRows]);

  const handleNavigateToMRP = () => {
    if (isSplitView && applyGrouping && groupingKeys.includes("item")) {
      const selectedGroupedList = selectedGroupedRows.map((row: any) => {
        const itemInfo = itemsMap[row.itemId] || itemsMap[row.itemName] || row;
        return {
          itemId: row.itemId,
          itemName: row.itemName,
          quantity: Number(row.quantity || 0),
          soQuantity: Number(row.quantity || 0),
          pendingQuantity: Number(row.pendingQuantity !== undefined ? row.pendingQuantity : row.quantity || 0),
          unit: (itemInfo?.metricsUnit ? uomMap[itemInfo.metricsUnit] : null) || row.unit || itemInfo?.unit || itemInfo?.uom || "Nos",
          alternateUnits: row.alternateUnits || itemInfo?.alternateUnits || itemInfo?.alternateUnit || [],
        };
      });

      navigate("/material_planning", {
        state: {
          fromSalesDashboardDetails: true,
          selectedGroupedItems: selectedGroupedList,
        },
      });
      return;
    }

    if (isSplitView) {
      const salesOrderNumbers = Array.from(
        new Set(selectedItemDocs.map((item: any) => item.documentNumber).filter(Boolean))
      );
      const selectedItemIds = Array.from(
        new Set(selectedItemDocs.map((item: any) => item.itemId).filter(Boolean))
      );

      const itemQuantities: Record<string, number> = {};
      const selectedItemsList: any[] = [];

      selectedItemDocs.forEach((item: any) => {
        const factor = getItemConversionFactorToBase(item);
        const qty = Number(item?.quantity || 0) * factor;
        const recQty = Number(item?.receivedQuantity || item?.challanQuantity || 0) * factor;
        const invQty = Number(item?.pendingQuantity || item?.invoiceQuantity || 0) * factor;
        const netQty = calculatePendingQuantity(qty, recQty, invQty);

        const docItemKey = `${item.documentNumber}_${item.itemId}`;
        itemQuantities[docItemKey] = (itemQuantities[docItemKey] || 0) + netQty;
        itemQuantities[item.itemId] = (itemQuantities[item.itemId] || 0) + netQty;

        selectedItemsList.push({
          ...item,
          quantity: netQty,
        });
      });

      navigate("/material_planning", {
        state: {
          selectedSalesOrders: salesOrderNumbers,
          selectedItemIds: selectedItemIds,
          itemQuantities: itemQuantities,
          selectedItems: selectedItemsList,
        },
      });
    } else {
      const salesOrderNumbers = selectedDocs.map((doc: any) => doc.documentNumber);
      navigate("/material_planning", {
        state: {
          selectedSalesOrders: salesOrderNumbers,
        },
      });
    }
  };

  const formatDateYear = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "N/A";
      return String(date.getFullYear());
    } catch {
      return "N/A";
    }
  };

  const formatDateMonth = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-IN", { month: "short", year: "numeric" });
    } catch {
      return "N/A";
    }
  };

  const COMPARISON_OPTIONS = [
    { label: "<", value: "lt" },
    { label: "≤", value: "lte" },
    { label: "=", value: "eq" },
    { label: "≠", value: "ne" },
    { label: "≥", value: "gte" },
    { label: ">", value: "gt" },
  ];

  // Helper Component for Numeric Column Filter Dropdown
  const NumericFilterDropdown: React.FC<{
    setSelectedKeys: (keys: React.Key[]) => void;
    selectedKeys: React.Key[];
    confirm: () => void;
    clearFilters?: () => void;
    label: string;
  }> = ({ setSelectedKeys, selectedKeys, confirm, clearFilters, label }) => {
    const raw = selectedKeys && selectedKeys[0] ? String(selectedKeys[0]) : "";
    let initialOp = "eq";
    let initialVal1 = "";
    let initialVal2 = "";

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        initialOp = parsed.op || "eq";
        initialVal1 = parsed.val1 !== undefined ? String(parsed.val1) : "";
        initialVal2 = parsed.val2 !== undefined ? String(parsed.val2) : "";
      } catch {
        const parts = raw.split(":");
        initialOp = parts[0] || "eq";
        initialVal1 = parts[1] || "";
        initialVal2 = parts[2] || "";
      }
    }

    const [operator, setOperator] = useState(initialOp);
    const [val1, setVal1] = useState(initialVal1);
    const [val2, setVal2] = useState(initialVal2);

    useEffect(() => {
      if (!raw) {
        setOperator("eq");
        setVal1("");
        setVal2("");
      }
    }, [raw]);

    const handleSearch = () => {
      if (val1 === "" && (operator !== "range" || val2 === "")) {
        clearFilters?.();
        confirm();
        return;
      }
      const filterVal = JSON.stringify({ op: operator, val1, val2 });
      setSelectedKeys([filterVal]);
      confirm();
    };

    const handleReset = () => {
      setOperator("eq");
      setVal1("");
      setVal2("");
      clearFilters?.();
      confirm();
    };

    return (
      <div style={{ padding: 10, width: 220 }}>
        <div style={{ marginBottom: 6, fontWeight: 500, fontSize: "12px", color: "#475569" }}>
          Filter {label}
        </div>
        <Select
          size="small"
          value={operator}
          onChange={(val) => setOperator(val)}
          style={{ width: "100%", marginBottom: 8 }}
          options={COMPARISON_OPTIONS}
        />
        {operator === "range" ? (
          <Space style={{ marginBottom: 8, width: "100%" }}>
            <Input
              size="small"
              type="number"
              placeholder="Min"
              value={val1}
              onChange={(e) => setVal1(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 95 }}
            />
            <Input
              size="small"
              type="number"
              placeholder="Max"
              value={val2}
              onChange={(e) => setVal2(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 95 }}
            />
          </Space>
        ) : (
          <Input
            size="small"
            type="number"
            placeholder={`Enter value`}
            value={val1}
            onChange={(e) => setVal1(e.target.value)}
            onPressEnter={handleSearch}
            style={{ marginBottom: 8, width: "100%" }}
          />
        )}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            style={{ flex: 1 }}
          >
            Search
          </Button>
          <Button
            size="small"
            onClick={handleReset}
            style={{ flex: 1 }}
          >
            Reset
          </Button>
        </div>
      </div>
    );
  };

  // Helper for Numeric Column-Level Search Filters
  const getNumericColumnSearchProps = (dataIndex: string, label: string) => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <NumericFilterDropdown
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          label={label}
        />
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        if (!value) return true;
        let op = "eq";
        let val1 = "";
        let val2 = "";

        try {
          const parsed = typeof value === "string" ? JSON.parse(value) : value;
          op = parsed.op || "eq";
          val1 = parsed.val1;
          val2 = parsed.val2;
        } catch {
          const parts = String(value).split(":");
          op = parts[0] || "eq";
          val1 = parts[1] || "";
          val2 = parts[2] || "";
        }

        const num1 = parseFloat(val1);
        const num2 = parseFloat(val2);
        const recordVal = Number(record[dataIndex] || 0);

        if (isNaN(recordVal)) return false;

        switch (op) {
          case "lt":
            return !isNaN(num1) && recordVal < num1;
          case "lte":
            return !isNaN(num1) && recordVal <= num1;
          case "gt":
            return !isNaN(num1) && recordVal > num1;
          case "gte":
            return !isNaN(num1) && recordVal >= num1;
          case "eq":
            return !isNaN(num1) && recordVal === num1;
          case "ne":
            return !isNaN(num1) && recordVal !== num1;
          case "range": {
            const hasMin = !isNaN(num1);
            const hasMax = !isNaN(num2);
            if (hasMin && hasMax) return recordVal >= num1 && recordVal <= num2;
            if (hasMin) return recordVal >= num1;
            if (hasMax) return recordVal <= num2;
            return true;
          }
          default:
            return true;
        }
      },
    };
  };

  // Helper for Column-Level Search Filters
  const getColumnSearchProps = (dataIndex: string, label: string) => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8, width: 220 }}>
          <Input
            placeholder={`Search ${label}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
            size="small"
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 95 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
              size="small"
              style={{ width: 95 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        if (!value) return true;
        const valStr = String(value).trim().toLowerCase();

        if (dataIndex === "partyName" || dataIndex === "buyerName") {
          const party = String(record?.buyerName || record?.supplierName || "").toLowerCase();
          return party.includes(valStr);
        }

        if (dataIndex === "tags" || dataIndex === "labels") {
          const tags = Array.isArray(record?.labels)
            ? record?.labels
            : (isValidJSON(record?.labels) || []);
          const labelsStr = tags
            .map((t: any) => (typeof t === "string" ? t : t?.label || ""))
            .join(" ")
            .toLowerCase();
          return labelsStr.includes(valStr);
        }

        if (dataIndex === "documentDate" || dataIndex === "purchaseOrderDate" || dataIndex === "createdAt") {
          const rawDate = record[dataIndex];
          const formatted = formatDate(rawDate);
          return (
            String(rawDate || "").toLowerCase().includes(valStr) ||
            formatted.toLowerCase().includes(valStr)
          );
        }

        if (dataIndex === "itemStatus") {
          const statusStr = (Number(record?.pendingQuantity || 0) !== 0 ? "pending" : "completed").toLowerCase();
          return statusStr.includes(valStr);
        }

        return String(record[dataIndex] || "").toLowerCase().includes(valStr);
      },
    };
  };

  // Grouping logic for both Document Level and Item Level
  const handleApplyGrouping = (keys: string[], withUnit: boolean = separateWithUnit) => {
    if (!keys || keys.length === 0) {
      setApplyGrouping(false);
      setGroupingData([]);
      setGroupingColumns([]);
      return;
    }

    if (!isSplitView) {
      // Document Level Grouping
      const groupedMap: Record<string, any> = {};

      filteredData.forEach((doc: any) => {
        const keyParts: string[] = [];
        if (keys.includes("customer")) keyParts.push(doc.buyerName || doc.supplierName || "Unknown");
        if (keys.includes("docType")) keyParts.push(doc.documentType || "N/A");
        if (keys.includes("year")) keyParts.push(formatDateYear(doc.createdAt));
        if (keys.includes("month")) keyParts.push(formatDateMonth(doc.createdAt));
        if (keys.includes("status")) keyParts.push(AppConstants?.DOCUMENT_STATUS?.[doc.status] || "N/A");

        const groupKey = keyParts.join(" | ") || "All";

        if (!groupedMap[groupKey]) {
          groupedMap[groupKey] = {
            groupKey,
            buyerName: doc.buyerName || doc.supplierName || "Unknown",
            documentType: doc.documentType || "N/A",
            yearGroup: formatDateYear(doc.createdAt),
            monthGroup: formatDateMonth(doc.createdAt),
            statusGroup: AppConstants?.DOCUMENT_STATUS?.[doc.status] || "N/A",
            documentCount: 0,
            totalItemsCount: 0,
            totalAmount: 0,
          };
        }

        const group = groupedMap[groupKey];
        group.documentCount += 1;

        let rawItems = doc.items || doc.itemDetails || doc.documentItems;
        if (typeof rawItems === "string") {
          try {
            const parsed = JSON.parse(rawItems);
            if (Array.isArray(parsed)) rawItems = parsed;
            else rawItems = [];
          } catch {
            rawItems = [];
          }
        }
        if (Array.isArray(rawItems)) {
          group.totalItemsCount += rawItems.length;
          rawItems.forEach((it: any) => {
            const qty = Number(it.quantity || it.qty || 0);
            const rate = Number(it.rate || it.unitPrice || it.price || 0);
            const taxPct = Number(it.gstRate || it.taxPercent || it.tax || 0);
            const totalBefore = Number(it.totalBeforeTax || (qty * rate));
            const totalTax = Number(it.totalTax || (totalBefore * taxPct / 100));
            const totalAfter = Number(it.totalAfterTax || (totalBefore + totalTax));
            group.totalAmount += totalAfter;
          });
        }
      });

      const groupedList = Object.values(groupedMap);
      const dynamicCols: any[] = [];

      if (keys.includes("customer")) {
        dynamicCols.push({
          title: "Customer Name",
          dataIndex: "buyerName",
          key: "buyerName",
          width: 220,
          sorter: (a: any, b: any) => String(a.buyerName).localeCompare(String(b.buyerName)),
          ...getColumnSearchProps("buyerName", "Customer Name"),
        });
      }
      if (keys.includes("docType")) {
        dynamicCols.push({
          title: "Document Type",
          dataIndex: "documentType",
          key: "documentType",
          width: 160,
          render: (text: string) => <Tag color="blue">{text}</Tag>,
          ...getColumnSearchProps("documentType", "Document Type"),
        });
      }
      if (keys.includes("year")) {
        dynamicCols.push({
          title: "Year",
          dataIndex: "yearGroup",
          key: "yearGroup",
          width: 120,
          sorter: (a: any, b: any) => String(a.yearGroup).localeCompare(String(b.yearGroup)),
          ...getColumnSearchProps("yearGroup", "Year"),
        });
      }
      if (keys.includes("month")) {
        dynamicCols.push({
          title: "Month",
          dataIndex: "monthGroup",
          key: "monthGroup",
          width: 140,
          ...getColumnSearchProps("monthGroup", "Month"),
        });
      }
      if (keys.includes("status")) {
        dynamicCols.push({
          title: "Status",
          dataIndex: "statusGroup",
          key: "statusGroup",
          width: 140,
          render: (text: string) => <Tag color="default">{text}</Tag>,
          ...getColumnSearchProps("statusGroup", "Status"),
        });
      }

      dynamicCols.push(
        {
          title: "Document Count",
          dataIndex: "documentCount",
          key: "documentCount",
          width: 150,
          align: "right" as const,
          sorter: (a: any, b: any) => a.documentCount - b.documentCount,
          ...getNumericColumnSearchProps("documentCount", "Document Count"),
        },
        {
          title: "Total Items",
          dataIndex: "totalItemsCount",
          key: "totalItemsCount",
          width: 140,
          align: "right" as const,
          sorter: (a: any, b: any) => a.totalItemsCount - b.totalItemsCount,
          ...getNumericColumnSearchProps("totalItemsCount", "Total Items"),
        },
        {
          title: "Total Amount (₹)",
          dataIndex: "totalAmount",
          key: "totalAmount",
          width: 180,
          align: "right" as const,
          render: (v: number) => `₹ ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          sorter: (a: any, b: any) => a.totalAmount - b.totalAmount,
          ...getNumericColumnSearchProps("totalAmount", "Total Amount"),
        }
      );

      setGroupingData(groupedList);
      setGroupingColumns(dynamicCols);
      setApplyGrouping(true);

    } else {
      // Item Level Grouping
      const groupedMap: Record<string, any> = {};

      filteredItemsList.forEach((item: any) => {
        const keyParts: string[] = [];
        if (keys.includes("customer")) keyParts.push(item.buyerName || "Unknown");
        if (keys.includes("item")) {
          keyParts.push(item.itemName || "Unknown Item");
          if (withUnit) {
            const rawUnit = item.unit || item.UOM || item.uom;
            const itemUnit = rawUnit && rawUnit !== "N/A" ? String(rawUnit).trim() : "N/A";
            keyParts.push(`Unit:${itemUnit}`);
          }
        }
        if (keys.includes("store")) keyParts.push(item.store || "N/A");
        if (keys.includes("year")) keyParts.push(formatDateYear(item.documentDate));
        if (keys.includes("month")) keyParts.push(formatDateMonth(item.documentDate));

        const groupKey = keyParts.join(" | ") || "All";

        const itmInfo = itemsMap[item.itemId] ||
          itemsMap[String(item.itemId || "").toLowerCase()] ||
          itemsMap[item.itemName] ||
          itemsMap[String(item.itemName || "").toLowerCase()] ||
          item;
        const masterPrimaryUnit = (itmInfo?.metricsUnit ? uomMap[itmInfo.metricsUnit] : null) || itmInfo?.unit || itmInfo?.uom || itmInfo?.UOM;

        const rawItemUnit = item.unit || item.UOM || item.uom;
        const currentUnit = (withUnit && rawItemUnit && rawItemUnit !== "N/A")
          ? String(rawItemUnit).trim()
          : (masterPrimaryUnit || item.unit || item.UOM || item.uom || "N/A");

        if (!groupedMap[groupKey]) {
          groupedMap[groupKey] = {
            groupKey,
            buyerName: item.buyerName || "Unknown",
            itemName: item.itemName || "Unknown Item",
            itemId: item.itemId || "N/A",
            unit: currentUnit,
            UOM: currentUnit,
            separateWithUnit: withUnit,
            isBaseUnit: !withUnit,
            alternateUnits: itmInfo?.alternateUnits || itmInfo?.alternateUnit || item.alternateUnits || item.alternateUnit || [],
            store: item.store || "N/A",
            yearGroup: formatDateYear(item.documentDate),
            monthGroup: formatDateMonth(item.documentDate),
            recordCount: 0,
            quantity: 0,
            challanQuantity: 0,
            invoiceQuantity: 0,
            pendingQuantity: 0,
            totalAfterTax: 0,
          };
        }

        // Check if item.UOM is alternate unit or base unit and convert accordingly
        const factor = withUnit ? 1 : getItemConversionFactorToBase(item, itmInfo);
        const itmQty = Number(item.quantity || 0) * factor;
        const itmChallan = Number(item.receivedQuantity || item.challanQuantity || 0) * factor;
        const itmInvoice = Number(item.invoiceQuantity || 0) * factor;
        const itmPending = calculatePendingQuantity(itmQty, itmChallan, itmInvoice);

        const group = groupedMap[groupKey];
        group.recordCount += 1;
        group.quantity += itmQty;
        group.challanQuantity += itmChallan;
        group.invoiceQuantity += itmInvoice;
        group.pendingQuantity += itmPending;
        group.totalAfterTax += Number(item.totalAfterTax || 0);
        if (!group.unit || group.unit === "N/A") {
          group.unit = currentUnit;
        }
        if (!group.UOM || group.UOM === "N/A") {
          group.UOM = currentUnit;
        }
        if (!group.alternateUnits || group.alternateUnits.length === 0) {
          group.alternateUnits = itmInfo?.alternateUnits || itmInfo?.alternateUnit || item.alternateUnits || item.alternateUnit || [];
        }
      });

      const groupedList = Object.values(groupedMap);
      const dynamicCols: any[] = [];
      const isGroupedByItem = keys.includes("item");

      if (keys.includes("customer")) {
        dynamicCols.push({
          title: "Customer Name",
          dataIndex: "buyerName",
          key: "buyerName",
          width: 220,
          sorter: (a: any, b: any) => String(a.buyerName).localeCompare(String(b.buyerName)),
          ...getColumnSearchProps("buyerName", "Customer Name"),
        });
      }
      if (keys.includes("item")) {
        dynamicCols.push(
          {
            title: "Item Name",
            dataIndex: "itemName",
            key: "itemName",
            width: 200,
            sorter: (a: any, b: any) => String(a.itemName).localeCompare(String(b.itemName)),
            ...getColumnSearchProps("itemName", "Item Name"),
          },
          {
            title: "Item ID",
            dataIndex: "itemId",
            key: "itemId",
            width: 130,
            ...getColumnSearchProps("itemId", "Item ID"),
          },
          ...(withUnit ? [{
            title: "UOM",
            dataIndex: "unit",
            key: "unit",
            width: 100,
            sorter: (a: any, b: any) => String(a.unit || "").localeCompare(String(b.unit || "")),
            ...getColumnSearchProps("unit", "UOM"),
            render: (text: string) => <Tag color="blue">{text || "N/A"}</Tag>,
          }] : [])
        );
      }
      if (keys.includes("store")) {
        dynamicCols.push({
          title: "Store",
          dataIndex: "store",
          key: "store",
          width: 120,
          ...getColumnSearchProps("store", "Store"),
        });
      }
      if (keys.includes("year")) {
        dynamicCols.push({
          title: "Year",
          dataIndex: "yearGroup",
          key: "yearGroup",
          width: 120,
          sorter: (a: any, b: any) => String(a.yearGroup).localeCompare(String(b.yearGroup)),
          ...getColumnSearchProps("yearGroup", "Year"),
        });
      }
      if (keys.includes("month")) {
        dynamicCols.push({
          title: "Month",
          dataIndex: "monthGroup",
          key: "monthGroup",
          width: 140,
          ...getColumnSearchProps("monthGroup", "Month"),
        });
      }

      dynamicCols.push(
        {
          title: "Record Count",
          dataIndex: "recordCount",
          key: "recordCount",
          width: 130,
          align: "right" as const,
          sorter: (a: any, b: any) => a.recordCount - b.recordCount,
          ...getNumericColumnSearchProps("recordCount", "Record Count"),
        },
        {
          title: "Total SO Qty",
          dataIndex: "quantity",
          key: "quantity",
          width: isGroupedByItem ? 175 : 130,
          align: "right" as const,
          sorter: (a: any, b: any) => a.quantity - b.quantity,
          ...getNumericColumnSearchProps("quantity", "Total SO Qty"),
          render: (v: number, record: any) =>
            isGroupedByItem ? renderQuantityWithAllUnits(v, record) : Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 }),
        },
        ...(isDispatchBasedOnChallan
          ? [
              {
                title: "Total Delivery Challan Qty",
                dataIndex: "challanQuantity",
                key: "challanQuantity",
                width: isGroupedByItem ? 175 : 150,
                align: "right" as const,
                sorter: (a: any, b: any) => a.challanQuantity - b.challanQuantity,
                ...getNumericColumnSearchProps("challanQuantity", "Total Delivery Challan Qty"),
                render: (v: number, record: any) =>
                  isGroupedByItem ? renderQuantityWithAllUnits(v, record) : Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 }),
              },
            ]
          : [
              {
                title: "Total Invoice Qty",
                dataIndex: "invoiceQuantity",
                key: "invoiceQuantity",
                width: isGroupedByItem ? 175 : 150,
                align: "right" as const,
                sorter: (a: any, b: any) => a.invoiceQuantity - b.invoiceQuantity,
                ...getNumericColumnSearchProps("invoiceQuantity", "Total Invoice Qty"),
                render: (v: number, record: any) =>
                  isGroupedByItem ? renderQuantityWithAllUnits(v, record) : Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 }),
              },
            ]),
        {
          title: "Total Pending Qty",
          dataIndex: "pendingQuantity",
          key: "pendingQuantity",
          width: isGroupedByItem ? 175 : 150,
          align: "right" as const,
          sorter: (a: any, b: any) => a.pendingQuantity - b.pendingQuantity,
          ...getNumericColumnSearchProps("pendingQuantity", "Total Pending Qty"),
          render: (v: number, record: any) =>
            isGroupedByItem ? renderQuantityWithAllUnits(v, record) : Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 }),
        },
        {
          title: "Total Amount (₹)",
          dataIndex: "totalAfterTax",
          key: "totalAfterTax",
          width: 180,
          align: "right" as const,
          render: (v: number) => `₹ ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          sorter: (a: any, b: any) => a.totalAfterTax - b.totalAfterTax,
          ...getNumericColumnSearchProps("totalAfterTax", "Total Amount"),
        }
      );

      setGroupingData(groupedList);
      setGroupingColumns(dynamicCols);
      setApplyGrouping(true);
    }
  };

  useEffect(() => {
    if (applyGrouping && groupingKeys.length > 0) {
      handleApplyGrouping(groupingKeys, separateWithUnit);
    }
  }, [filteredData, filteredItemsList, isSplitView, itemsMap, uomMap, separateWithUnit, isDispatchBasedOnChallan]);

  // Column definitions for main documents table
  const columns = useMemo(() => {
    const isMultiType = selectedTypes.length > 1;

    const cols: any[] = [
      {
        title: "Document No.",
        dataIndex: "documentNumber",
        key: "documentNumber",
        width: 170,
        ...getColumnSearchProps("documentNumber", "Document No."),
        render: (text: string, record: any) => (
          <Link
            className="link"
            style={{ fontWeight: "600", color: "#0167ff" }}
            to={`/previewDocument?documentNumber=${record?.documentNumber}`}
          >
            {record?.documentNumber || "N/A"}
          </Link>
        ),
      },
    ];

    if (isMultiType) {
      cols.push({
        title: "Document Type",
        dataIndex: "documentType",
        key: "documentType",
        width: 150,
        ...getColumnSearchProps("documentType", "Document Type"),
        render: (text: string, record: any) => (
          <Tag color="blue">{record?.documentType || "N/A"}</Tag>
        ),
      });
    }

    const partyTitle = "Customer Name";

    cols.push(
      {
        title: partyTitle,
        key: "partyName",
        width: 220,
        ...getColumnSearchProps("partyName", partyTitle),
        render: (text: string, record: any) => (
          <span>{record?.buyerName || record?.supplierName || "N/A"}</span>
        ),
      },
      {
        title: "Created Date",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 130,
        ...getColumnSearchProps("createdAt", "Created Date"),
        render: (text: string, record: any) => <span>{formatDate(record?.createdAt)}</span>,
      }
    );

    cols.push({
      title: "Labels",
      dataIndex: "tags",
      key: "tags",
      width: 200,
      ...getColumnSearchProps("tags", "Labels"),
      render: (text: string, record: any) => {
        const tags = Array.isArray(record?.labels)
          ? record?.labels
          : (isValidJSON(record?.labels) || []);
        return (
          <div>
            {tags.map((tag: any, index: number) => {
              const labelText = typeof tag === "string" ? tag : (tag?.label || "");
              const labelColor = typeof tag === "string" ? undefined : (tag?.colour || tag?.color);
              return (
                <Tag key={index} color={labelColor}>
                  {labelText}
                </Tag>
              );
            })}
          </div>
        );
      },
    });

    return cols;
  }, [selectedTypes]);

  // Column definitions for Item Level Split View table (Reference from Reports.tsx for Sales Orders)
  const allItemColumns: any[] = useMemo(
    () => [
      {
        title: "Customer Name",
        dataIndex: "buyerName",
        key: "buyerName",
        width: 170,
        sorter: (a: any, b: any) =>
          String(a?.buyerName || "").localeCompare(String(b?.buyerName || "")),
        ...getColumnSearchProps("buyerName", "Customer Name"),
      },
      {
        title: "Sales Order Number",
        dataIndex: "documentNumber",
        key: "documentNumber",
        width: 170,
        sorter: (a: any, b: any) =>
          String(a?.documentNumber || "").localeCompare(String(b?.documentNumber || "")),
        ...getColumnSearchProps("documentNumber", "Sales Order Number"),
        render: (text: string): any => (
          <Link
            className="link"
            style={{ fontWeight: "600", color: "#0167ff" }}
            to={`/previewDocument?documentNumber=${text}`}
          >
            {text || "N/A"}
          </Link>
        ),
      },
      {
        title: "Sales Order Date",
        dataIndex: "documentDate",
        key: "documentDate",
        width: 140,
        ...getColumnSearchProps("documentDate", "Sales Order Date"),
        render: (text: string): any => formatDate(text),
      },
      {
        title: "Customer PO Number",
        dataIndex: "purchaseOrderNumber",
        key: "purchaseOrderNumber",
        width: 170,
        ...getColumnSearchProps("purchaseOrderNumber", "Customer PO Number"),
      },
      {
        title: "Customer PO Date",
        dataIndex: "purchaseOrderDate",
        key: "purchaseOrderDate",
        width: 140,
        ...getColumnSearchProps("purchaseOrderDate", "Customer PO Date"),
        render: (text: string): any => formatDate(text),
      },
      {
        title: "Item Id",
        dataIndex: "itemId",
        key: "itemId",
        width: 120,
        ...getColumnSearchProps("itemId", "Item Id"),
      },
      {
        title: "Item Name",
        dataIndex: "itemName",
        key: "itemName",
        width: 180,
        sorter: (a: any, b: any) =>
          String(a?.itemName || "").localeCompare(String(b?.itemName || "")),
        ...getColumnSearchProps("itemName", "Item Name"),
      },
      {
        title: "Store",
        dataIndex: "store",
        key: "store",
        width: 100,
        ...getColumnSearchProps("store", "Store"),
      },
      {
        title: "HSN Code",
        dataIndex: "hsnCode",
        key: "hsnCode",
        width: 100,
        ...getColumnSearchProps("hsnCode", "HSN Code"),
      },
      {
        title: "UOM",
        dataIndex: "unit",
        key: "unit",
        width: 90,
        ...getColumnSearchProps("unit", "UOM"),
      },
      {
        title: "SO Quantity",
        dataIndex: "quantity",
        key: "quantity",
        width: 120,
        align: "right" as const,
        sorter: (a: any, b: any) => Number(a?.quantity || 0) - Number(b?.quantity || 0),
        ...getNumericColumnSearchProps("quantity", "SO Quantity"),
      },
      ...(isDispatchBasedOnChallan
        ? [
            {
              title: "Delivery Challan Quantity",
              dataIndex: "receivedQuantity",
              key: "receivedQuantity",
              width: 140,
              align: "right" as const,
              sorter: (a: any, b: any) => Number(a?.receivedQuantity || 0) - Number(b?.receivedQuantity || 0),
              ...getNumericColumnSearchProps("receivedQuantity", "Delivery Challan Quantity"),
            },
          ]
        : [
            {
              title: "Invoice Quantity",
              dataIndex: "invoiceQuantity",
              key: "invoiceQuantity",
              width: 130,
              align: "right" as const,
              sorter: (a: any, b: any) => Number(a?.invoiceQuantity || 0) - Number(b?.invoiceQuantity || 0),
              ...getNumericColumnSearchProps("invoiceQuantity", "Invoice Quantity"),
            },
          ]),
      {
        title: "Pending Quantity",
        dataIndex: "pendingQuantity",
        key: "pendingQuantity",
        width: 140,
        align: "right" as const,
        sorter: (a: any, b: any) => Number(a?.pendingQuantity || 0) - Number(b?.pendingQuantity || 0),
        ...getNumericColumnSearchProps("pendingQuantity", "Pending Quantity"),
      },
      {
        title: "Item Status",
        dataIndex: "itemStatus",
        key: "itemStatus",
        width: 120,
        ...getColumnSearchProps("itemStatus", "Item Status"),
        render: (text: any, record: any): any => (
          <Tag color={record?.pendingQuantity != 0 ? "warning" : "success"}>
            {record?.pendingQuantity != 0 ? "Pending" : "Completed"}
          </Tag>
        ),
      },
    ],
    [isDispatchBasedOnChallan]
  );

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
      if (selectedRows.length > 0) {
        setSelectedRecord(selectedRows[selectedRows.length - 1]);
      }
    },
  };

  const breadcrumbTitle =
    selectedTypes.length === 1
      ? `${["purchaseOrder", "purchaseInvoice"].includes(selectedTypes[0].typeKey) ? "Purchase" : "Sales"} KPI: ${selectedTypes[0].label}`
      : `KPI Overview (${selectedTypes.length} Metrics Selected)`;

  const breadcrumbItems = [
    { key: "dashboard", title: <Link to="/dashboard">Dashboard</Link> },
    { key: "salesDetails", title: breadcrumbTitle },
  ];

  const headerBg =
    selectedTypes.length === 1
      ? selectedTypes[0].bg
      : "linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(236, 72, 153, 0.08) 100%)";
  const headerBorder =
    selectedTypes.length === 1 ? `${selectedTypes[0].color}30` : "rgba(99, 102, 241, 0.2)";

  // Selected row count for export display
  const selectedExportCount = useMemo(() => {
    if (isSplitView) {
      return applyGrouping ? selectedGroupedRows.length : selectedItemDocs.length;
    }
    return applyGrouping ? 0 : selectedDocs.length;
  }, [isSplitView, applyGrouping, selectedGroupedRows.length, selectedItemDocs.length, selectedDocs.length]);

  // Download table data as XLSX with clean plain text (no HTML)
  const handleDownloadXLSX = () => {
    // 1. Identify active data and columns based on view state
    let activeData: any[] = [];
    let activeCols: any[] = [];

    if (isSplitView) {
      activeData = applyGrouping ? groupingData : filteredItemsList;
      activeCols = applyGrouping ? groupingColumns : allItemColumns;
    } else {
      activeData = applyGrouping ? groupingData : filteredData;
      activeCols = applyGrouping ? groupingColumns : columns;
    }

    if (!activeData || activeData.length === 0) {
      notification.open({
        type: "warning",
        message: "No Data to Export",
        description: "There are no records to download in the current view.",
        duration: 3,
      });
      return;
    }

    // 2. Identify rows to export (selected rows take precedence if any are selected)
    let rowsToExport: any[] = activeData;

    if (isSplitView) {
      if (applyGrouping) {
        if (selectedGroupedRows && selectedGroupedRows.length > 0) {
          rowsToExport = selectedGroupedRows;
        }
      } else {
        if (selectedItemDocs && selectedItemDocs.length > 0) {
          rowsToExport = selectedItemDocs;
        }
      }
    } else {
      if (!applyGrouping && selectedDocs && selectedDocs.length > 0) {
        rowsToExport = selectedDocs;
      }
    }

    if (!rowsToExport || rowsToExport.length === 0) {
      rowsToExport = activeData;
    }

    // 3. Filter out non-exportable columns (like action buttons or checkbox columns)
    const exportableCols = activeCols.filter((col: any) => {
      const headerTitle = convertToCleanPlainText(col.title);
      return (
        headerTitle.length > 0 &&
        col.key !== "action" &&
        col.dataIndex !== "action" &&
        col.key !== "selection"
      );
    });

    if (exportableCols.length === 0) {
      notification.open({
        type: "warning",
        message: "Export Failed",
        description: "No exportable columns found.",
        duration: 3,
      });
      return;
    }

    // 4. Map each row to clean plain text/number fields, converting any JSX/HTML to plain text
    const excelRows = rowsToExport.map((record: any, rowIndex: number) => {
      const rowObj: Record<string, any> = {};

      exportableCols.forEach((col: any) => {
        const colHeader = convertToCleanPlainText(col.title);
        const dataKey = col.dataIndex || col.key;

        // Custom handling for labels / tags to produce a clean comma-separated list
        if (dataKey === "tags" || dataKey === "labels") {
          const tags = Array.isArray(record?.labels)
            ? record?.labels
            : (isValidJSON(record?.labels) || []);
          const labelNames = tags
            .map((t: any) => (typeof t === "string" ? t : t?.label || ""))
            .filter(Boolean);
          rowObj[colHeader] = labelNames.join(", ");
          return;
        }

        // If column has custom render, call it and clean HTML/JSX to plain text
        if (typeof col.render === "function") {
          const rawVal = dataKey ? record?.[dataKey] : undefined;
          try {
            const rendered = col.render(rawVal, record, rowIndex);
            if (typeof rendered === "number") {
              rowObj[colHeader] = rendered;
            } else {
              rowObj[colHeader] = convertToCleanPlainText(rendered);
            }
          } catch {
            rowObj[colHeader] = convertToCleanPlainText(rawVal);
          }
        } else {
          const rawVal = dataKey ? record?.[dataKey] : "";
          if (typeof rawVal === "number") {
            rowObj[colHeader] = rawVal;
          } else {
            rowObj[colHeader] = convertToCleanPlainText(rawVal);
          }
        }
      });

      return rowObj;
    });

    // 5. Generate Worksheet & auto-fit column widths
    const worksheet = XLSX.utils.json_to_sheet(excelRows);

    const colWidths = exportableCols.map((col: any) => {
      const headerText = convertToCleanPlainText(col.title);
      let maxLen = headerText.length;
      excelRows.forEach((r) => {
        const valStr = String(r[headerText] ?? "");
        if (valStr.length > maxLen) {
          maxLen = valStr.length;
        }
      });
      return { wch: Math.min(Math.max(maxLen + 3, 12), 50) };
    });
    worksheet["!cols"] = colWidths;

    // 6. Build Workbook and export
    const workbook = XLSX.utils.book_new();
    const sheetName = (isSplitView ? "Item Level" : "Document Level").slice(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const primaryType =
      selectedTypes.length === 1 ? selectedTypes[0].label.replace(/\s+/g, "_") : "Sales_Overview";
    const viewMode = isSplitView ? "Item_View" : "Document_View";
    const groupSuffix = applyGrouping ? "_Grouped" : "";
    const dateStr = new Date().toISOString().slice(0, 10);
    const fileName = `${primaryType}_${viewMode}${groupSuffix}_${dateStr}.xlsx`;

    XLSX.writeFile(workbook, fileName);

    notification.open({
      type: "success",
      message: "Download Complete",
      description: `Successfully exported ${excelRows.length} record(s) to ${fileName}`,
      duration: 3,
    });
  };

  // Render main document table component
  const renderDocumentTable = () => {
    const tableData = applyGrouping ? groupingData : filteredData;
    const tableCols = applyGrouping ? groupingColumns : columns;

    return (
      <Table
        rowSelection={applyGrouping ? undefined : rowSelection}
        onRow={(record: any) => ({
          onClick: () => {
            if (!applyGrouping) {
              setSelectedRecord(record);
              const recKey = record?.id || record?.documentNumber;
              if (recKey && !selectedRowKeys.includes(recKey)) {
                setSelectedRowKeys([recKey]);
              }
            }
          },
          style: {
            cursor: applyGrouping ? "default" : "pointer",
            background:
              !applyGrouping && (selectedRecord?.id || selectedRecord?.documentNumber) === (record?.id || record?.documentNumber)
                ? "rgba(1, 103, 255, 0.06)"
                : undefined,
          },
        })}
        columns={tableCols}
        rowKey={(record) => record?.groupKey || record?.id || record?.documentNumber}
        dataSource={tableData}
        bordered
        pagination={{
          position: ["bottomLeft"],
          current: currentPage,
          pageSize: pageSize,
          total: tableData.length,
          showSizeChanger: true,
          showTotal: (total) => (
            <span style={{ marginLeft: 12 }}>Total: {total} Items</span>
          ),
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
        style={{ borderRadius: "12px", overflow: "hidden" }}
        scroll={{ x: "100%", y: "calc(100vh - 360px)" }}
      />
    );
  };

  const itemRowSelection = {
    selectedRowKeys: selectedItemRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedItemRowKeys(newSelectedRowKeys);
    },
  };

  const groupedItemRowSelection = {
    selectedRowKeys: selectedGroupedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedGroupedRowKeys(newSelectedRowKeys);
      setSelectedGroupedRows(selectedRows);
    },
  };

  // Render item table in full width when Item View is ON
  const renderItemTable = () => {
    const tableData = applyGrouping ? groupingData : filteredItemsList;
    const tableCols = applyGrouping ? groupingColumns : allItemColumns;
    const isItemGrouped = applyGrouping && groupingKeys.includes("item");

    return (
      tableData.length > 0 ? (
        <Table
          rowSelection={
            applyGrouping
              ? (isItemGrouped ? groupedItemRowSelection : undefined)
              : itemRowSelection
          }
          columns={tableCols}
          rowKey={(item) => item?.groupKey || item?.uniqueKey || item?.itemId}
          dataSource={tableData}
          bordered
          pagination={{
            position: ["bottomLeft"],
            current: currentPage,
            pageSize: pageSize,
            total: tableData.length,
            showSizeChanger: true,
            showTotal: (total) => (
              <span style={{ marginLeft: 12 }}>Total: {total} Items</span>
            ),
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          style={{ borderRadius: "12px", overflow: "hidden" }}
          scroll={{ x: 2400, y: "calc(100vh - 440px)" }}
          summary={(pageData) => {
            if (!pageData || pageData.length === 0) return null;

            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: "#f8fafc", fontWeight: 600 }}>
                  {!applyGrouping && <Table.Summary.Cell index={0} />}
                  {tableCols.map((col: any, colIdx: number) => {
                    const cellIndex = applyGrouping ? colIdx : colIdx + 1;
                    const dataIndex = col.dataIndex || col.key;

                    if (colIdx === 0) {
                      return (
                        <Table.Summary.Cell index={cellIndex} key={col.key || colIdx}>
                          <Text strong style={{ color: "#1e293b" }}>
                            Total
                          </Text>
                        </Table.Summary.Cell>
                      );
                    }

                    const isNumeric =
                      col.align === "right" ||
                      [
                        "quantity",
                        "receivedQuantity",
                        "challanQuantity",
                        "invoiceQuantity",
                        "pendingQuantity",
                        "recordCount",
                        "totalAfterTax",
                        "totalAmount",
                      ].includes(dataIndex);

                    if (isNumeric) {
                      const total = pageData.reduce((sum, current) => {
                        const val = Number(current?.[dataIndex] || 0);
                        return sum + (isNaN(val) ? 0 : val);
                      }, 0);

                      let formattedTotal = "";
                      if (dataIndex === "totalAfterTax" || dataIndex === "totalAmount") {
                        formattedTotal = `₹ ${Number(total || 0).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`;
                      } else {
                        formattedTotal = Number(
                          Number(total || 0).toFixed(2)
                        ).toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        });
                      }

                      return (
                        <Table.Summary.Cell
                          index={cellIndex}
                          key={col.key || colIdx}
                          align="right"
                        >
                          <Text strong style={{ color: "#1e293b" }}>
                            {formattedTotal}
                          </Text>
                        </Table.Summary.Cell>
                      );
                    }

                    return (
                      <Table.Summary.Cell index={cellIndex} key={col.key || colIdx}>
                        <span></span>
                      </Table.Summary.Cell>
                    );
                  })}
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      ) : (
        <div style={{ textAlign: "center", padding: "64px 0", background: "#fafafa", borderRadius: "12px" }}>
          <img src={noResult} alt="No records found" style={{ width: "120px", marginBottom: "16px" }} />
          <p style={{ fontSize: "16px", color: "#94a3b8", margin: 0 }}>No item level records found matching current criteria.</p>
        </div>
      )
    );
  };

  return (
    <>
      <Loader loading={loading} />

      {/* Grouping Modal */}
      <Modal
        title="Grouping Options"
        open={openGroupingModal}
        onCancel={() => setOpenGroupingModal(false)}
        footer={null}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", paddingTop: "10px" }}>
          <div style={{ fontWeight: 500, fontSize: "14px", color: "#555" }}>
            Select Grouping Options ({isSplitView ? "Item Level View" : "Document Level View"}):
          </div>

          <Checkbox.Group
            value={groupingKeys}
            onChange={(values: any) => {
              setGroupingKeys(values);
              if (!values.includes("item")) {
                setSeparateWithUnit(false);
              }
            }}
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <Checkbox value="customer" style={{ fontSize: "14px" }}>
              Customer Name
            </Checkbox>
            {!isSplitView && (
              <Checkbox value="docType" style={{ fontSize: "14px" }}>
                Document Type
              </Checkbox>
            )}
            {isSplitView && (
              <>
                <Checkbox value="item" style={{ fontSize: "14px" }}>
                  Item Name
                </Checkbox>
                {groupingKeys.includes("item") && (
                  <div
                    style={{
                      marginLeft: "24px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      background: "#f8fafc",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                      width: "fit-content",
                    }}
                  >
                    <span style={{ fontSize: "13px", color: "#475569", fontWeight: 500 }}>
                      Separate with Unit:
                    </span>
                    <Switch
                      size="small"
                      checked={separateWithUnit}
                      onChange={(checked) => setSeparateWithUnit(checked)}
                    />
                  </div>
                )}
                <Checkbox value="store" style={{ fontSize: "14px" }}>
                  Store
                </Checkbox>
              </>
            )}
            <Checkbox value="year" style={{ fontSize: "14px" }}>
              Year
            </Checkbox>
            <Checkbox value="month" style={{ fontSize: "14px" }}>
              Month
            </Checkbox>
            {!isSplitView && (
              <Checkbox value="status" style={{ fontSize: "14px" }}>
                Status
              </Checkbox>
            )}
          </Checkbox.Group>

          <Divider style={{ margin: "10px 0" }} />

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            {applyGrouping && (
              <Button
                onClick={() => {
                  setApplyGrouping(false);
                  setGroupingData([]);
                  setGroupingColumns([]);
                  setGroupingKeys([]);
                  setSeparateWithUnit(false);
                  setOpenGroupingModal(false);
                }}
              >
                Clear Grouping
              </Button>
            )}
            <Button
              type="primary"
              disabled={groupingKeys.length === 0}
              onClick={() => {
                setOpenGroupingModal(false);
                handleApplyGrouping(groupingKeys, separateWithUnit);
              }}
            >
              Apply Grouping
            </Button>
          </div>
        </div>
      </Modal>

      <Layout className="layout" style={{ background: "#f8fafc", padding: "24px" }}>
        {/* Breadcrumb Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <Breadcrumb items={breadcrumbItems} />
          <Space size={12} align="center">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadXLSX}
              style={{ background: "#0284c7", borderColor: "#0284c7" }}
            >
              {selectedExportCount > 0
                ? `Download XLSX (${selectedExportCount})`
                : "Download XLSX"}
            </Button>
            <Button
              type="primary"
              icon={<ArrowLeftOutlined style={{ color: "#ffffff" }} />}
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </Button>
          </Space>
        </div>

        <Content
          style={{
            background: "#ffffff",
            padding: "24px",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
          }}
        >
          {/* Header Bar with KPI Badges */}


          {/* Filtering and Actions Toolbar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <Space wrap size={12} align="center">
              <Select
                mode="multiple"
                style={{ minWidth: 260, maxWidth: 450 }}
                placeholder="Select metric view(s)"
                value={selectedTypeIndices}
                onChange={handleDropdownChange}
                options={SALES_TYPES.map((t) => ({ label: t.label, value: t.value }))}
              />
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                style={{ width: 280 }}
              />
              <Space align="center" style={{ background: "#f1f5f9", padding: "4px 12px", borderRadius: "8px" }}>
                <span style={{ fontWeight: 500, color: "#475569", fontSize: "13px" }}>Item View:</span>
                <Switch
                  checked={isSplitView}
                  onChange={(checked) => {
                    setIsSplitView(checked);
                  }}
                />
              </Space>
              {applyGrouping && isSplitView && groupingKeys.includes("item") && (
                <Space align="center" style={{ background: "#f1f5f9", padding: "4px 12px", borderRadius: "8px" }}>
                  <span style={{ fontWeight: 500, color: "#475569", fontSize: "13px" }}>Separate with Unit:</span>
                  <Switch
                    checked={separateWithUnit}
                    onChange={(checked) => {
                      setSeparateWithUnit(checked);
                      handleApplyGrouping(groupingKeys, checked);
                    }}
                  />
                </Space>
              )}
              {applyGrouping && (
                <Button
                  type={applyGrouping ? "primary" : "default"}
                  icon={<AppstoreOutlined />}
                  onClick={() => setOpenGroupingModal(true)}
                  style={{ background: "#6366f1", borderColor: "#6366f1" }}
                >
                  Grouping Active
                </Button>
              )}
              {!applyGrouping && (
                <Button
                  type="default"
                  icon={<AppstoreOutlined />}
                  onClick={() => setOpenGroupingModal(true)}
                >
                  Grouping
                </Button>
              )}
              {(isAllSelectedSalesOrder || isGroupedMRPVisible) && (
                <Button
                  type="primary"
                  icon={<ScheduleOutlined />}
                  onClick={handleNavigateToMRP}
                  style={{ background: "#059669", borderColor: "#059669" }}
                >
                  {isGroupedMRPVisible
                    ? `MRP (${selectedGroupedRows.length} Items)`
                    : "MRP"}
                </Button>
              )}
            </Space>
            <Input
              placeholder={isSplitView ? "Search item, code, HSN or doc #" : "Search by Document # or Buyer/Supplier"}
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={isSplitView ? itemSearchText : globalSearchText}
              onChange={(e) => isSplitView ? setItemSearchText(e.target.value) : setGlobalSearchText(e.target.value)}
              style={{ width: 280 }}
              allowClear
            />
          </div>

          {/* Table: Document Level View (Switch OFF) vs Item Level View (Switch ON) */}
          {isSplitView ? (
            renderItemTable()
          ) : (applyGrouping ? groupingData.length > 0 : filteredData.length > 0) ? (
            renderDocumentTable()
          ) : (
            <div style={{ textAlign: "center", padding: "64px 0", background: "#fafafa", borderRadius: "12px" }}>
              <img src={noResult} alt="No records found" style={{ width: "120px", marginBottom: "16px" }} />
              <p style={{ fontSize: "16px", color: "#94a3b8", margin: 0 }}>No records found matching current criteria.</p>
            </div>
          )}
        </Content>
      </Layout>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  companyId: state.api.login?.data?.companyId,
  salesDetailsData: state.api.getSalesDashboardDetails?.salesDashboardDetails,
  loading: state.api.getSalesDashboardDetails?.loading,
  itemsData: state.api.getItems?.data?.data || state.api.getItems?.data,
  uomData: state.api.getUOM?.data,
  settingData: state.api.getSetting?.data || state.api.getSetting?.responseData || {},
});

const mapDispatchToProps = (dispatch: any) => ({
  getSalesDashboardDetails: (payload: any) => dispatch(getSalesDashboardDetails(payload)),
  getItems: (payload: any) => dispatch(getItems(payload)),
  getUOM: (payload: any) => dispatch(getUOM(payload)),
  getSetting: (payload: any) => dispatch(getSetting(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SalesDashboardDetails);
